/**
 * frontend/src/components/valuation/ValuationShellNav.tsx
 *
 * VAL-PATH-ROUTES (P1) — the "This engagement" step list of the left rail,
 * split out of ValuationShell.tsx. It draws the one step list (_steps.ts) for
 * this role: the same steps, labels, locks and next step as the step bar and
 * the dashboard card. A done step carries a dot; a locked step (owner, fewer
 * than two years read) shows but is not a link.
 */
import { Link } from 'react-router-dom'
import type { ValuationEngagement } from '@/types/valuation'
import { LOCKED_MESSAGE, currentStep, isLocked, stepsFor } from '@/pages/valuation/_steps'

export type ShellState = 'draft' | 'computed' | 'signed' | 'revising'

function Dot() {
  return (
    <span
      aria-hidden="true"
      className="ml-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
      style={{ backgroundColor: 'var(--good)' }}
    />
  )
}

export function EngagementNav({
  valuationId,
  pathname,
  hash,
  role,
  engagement,
  delivered,
}: {
  valuationId: string
  pathname: string
  hash: string
  role: string | undefined
  engagement: ValuationEngagement | null
  delivered: boolean
}) {
  const steps = stepsFor(role)
  const nextKey = engagement && !delivered ? currentStep(engagement, role).key : null
  const here = pathname + (hash === '#report' ? hash : '')
  return (
    <div className="flex-1 overflow-y-auto px-4 pb-4">
      <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-wide opacity-60">This engagement</div>
      <nav className="flex flex-col gap-0.5 text-sm" aria-label="Engagement sections">
        {steps.map(step => {
          const path = step.route(valuationId)
          const active = here === path
          const locked = isLocked(step.key, engagement, role)
          const isNext = step.key === nextKey
          const done = !!engagement && step.done(engagement)
          const content = (
            <span
              data-step={step.key}
              data-step-label={step.label}
              data-next={isNext ? 'true' : undefined}
              title={locked ? LOCKED_MESSAGE : undefined}
              className={`flex items-center justify-between rounded-r border-l-[3px] px-3 py-1.5 ${
                active ? 'bg-white/10 font-semibold' : ''
              } ${locked ? 'opacity-50' : active ? 'opacity-100' : 'opacity-80'}`}
              style={{ borderLeftColor: active || isNext ? 'var(--accent)' : 'transparent' }}
            >
              <span className="flex items-center gap-1.5">
                {step.label}
                {isNext && !active && (
                  <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">Next</span>
                )}
              </span>
              {done && !locked && <Dot />}
            </span>
          )
          return locked
            ? <div key={step.key}>{content}</div>
            : <Link key={step.key} to={path}>{content}</Link>
        })}
      </nav>
    </div>
  )
}
